import { useState } from "react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { Save, X, Plus, Trash2, Edit3 } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function AdminEditPanel({ profile, athleteId, onSave }) {
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editData, setEditData] = useState({});

  const startEdit = () => {
    setEditData({
      name: profile.name || "",
      height: profile.height || "",
      weight: profile.weight || "",
      age: profile.age || "",
      location: profile.location || "",
      school: profile.school || "",
      position_primary: profile.position_primary || "",
      position_secondary: profile.position_secondary || "",
      graduation_year: profile.graduation_year || "",
      bats_throws: profile.bats_throws || "",
      bio: profile.bio || "",
      level: profile.level || "",
      video_url: profile.video_url || "",
    });
    setIsEditing(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_URL}/api/admin/player/${athleteId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(editData),
      });
      if (res.ok) {
        toast.success("Player profile updated!");
        setIsEditing(false);
        if (onSave) onSave();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to update");
      }
    } catch (err) {
      toast.error("Update failed: " + err.message);
    } finally {
      setSaving(false);
    }
  };

  if (!isEditing) {
    return (
      <Button
        onClick={startEdit}
        data-testid="admin-edit-profile-btn"
        className="bg-blue-900 hover:bg-blue-800 text-white"
        size="sm"
      >
        <Edit3 className="h-4 w-4 mr-1" />
        Edit Profile
      </Button>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-lg border-2 border-blue-900 p-6 space-y-6" data-testid="admin-edit-panel">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-bold text-blue-900">Edit Player Profile</h3>
        <Button variant="ghost" size="sm" onClick={() => setIsEditing(false)}>
          <X className="h-4 w-4" />
        </Button>
      </div>

      <div className="grid grid-cols-2 gap-4">
        {[
          { key: "name", label: "Full Name", type: "text" },
          { key: "school", label: "School", type: "text" },
          { key: "position_primary", label: "Position (Primary)", type: "text", placeholder: "RHP, SS, CF..." },
          { key: "position_secondary", label: "Position (Secondary)", type: "text", placeholder: "3B, OF..." },
          { key: "graduation_year", label: "Graduation Year", type: "text" },
          { key: "age", label: "Age", type: "text", inputMode: "numeric" },
          { key: "height", label: "Height", type: "text", placeholder: "5'11\"" },
          { key: "weight", label: "Weight (lbs)", type: "text" },
          { key: "location", label: "Location", type: "text", placeholder: "City, State" },
          { key: "bats_throws", label: "Bats/Throws", type: "text", placeholder: "R/R" },
          { key: "level", label: "Level", type: "text", placeholder: "Varsity, JV..." },
          { key: "video_url", label: "Highlight Video URL", type: "url" },
        ].map(({ key, label, type, placeholder, inputMode }) => (
          <div key={key}>
            <label className="block text-xs font-semibold text-gray-600 mb-1">{label}</label>
            <input
              type={type}
              inputMode={inputMode}
              value={editData[key] || ""}
              placeholder={placeholder}
              onChange={(e) => setEditData({ ...editData, [key]: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-900 focus:border-transparent"
              data-testid={`edit-${key}`}
            />
          </div>
        ))}
      </div>

      <div>
        <label className="block text-xs font-semibold text-gray-600 mb-1">Bio / Notes</label>
        <textarea
          value={editData.bio || ""}
          onChange={(e) => setEditData({ ...editData, bio: e.target.value })}
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-900 focus:border-transparent"
          data-testid="edit-bio"
        />
      </div>

      <div className="flex justify-end gap-3">
        <Button variant="outline" onClick={() => setIsEditing(false)}>Cancel</Button>
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-blue-900 hover:bg-blue-800 text-white"
          data-testid="save-profile-btn"
        >
          <Save className="h-4 w-4 mr-1" />
          {saving ? "Saving..." : "Save Changes"}
        </Button>
      </div>
    </div>
  );
}

export function AdminMetricsEditor({ profile, athleteId, onSave }) {
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [metrics, setMetrics] = useState({});

  const startEdit = () => {
    const m = profile.metrics || {};
    setMetrics({
      fastball_velocity: m.fastball_velocity || "",
      avg_fastball_velocity: m.avg_fastball_velocity || "",
      spin_rate: m.spin_rate || "",
      extension: m.extension || "",
      exit_velocity: m.exit_velocity || "",
      sixty_time: m.sixty_time || "",
      pop_time: m.pop_time || "",
      usage_pct_fb: m.usage_pct_fb || "",
    });
    setIsEditing(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      // Convert empty strings to null, strings to numbers
      const cleaned = {};
      for (const [k, v] of Object.entries(metrics)) {
        if (v !== "" && v !== null && v !== undefined) {
          cleaned[k] = Number(v);
        }
      }
      const res = await fetch(`${API_URL}/api/admin/player/${athleteId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ metrics: cleaned }),
      });
      if (res.ok) {
        toast.success("Metrics updated!");
        setIsEditing(false);
        if (onSave) onSave();
      } else {
        toast.error("Failed to update metrics");
      }
    } catch (err) {
      toast.error("Update failed");
    } finally {
      setSaving(false);
    }
  };

  if (!isEditing) {
    return (
      <Button onClick={startEdit} variant="outline" size="sm" data-testid="admin-edit-metrics-btn">
        <Edit3 className="h-3.5 w-3.5 mr-1" /> Edit Metrics
      </Button>
    );
  }

  return (
    <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg space-y-4" data-testid="metrics-edit-panel">
      <h4 className="font-bold text-blue-900 text-sm">Edit Metrics</h4>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { key: "fastball_velocity", label: "Max FB Velo (mph)" },
          { key: "avg_fastball_velocity", label: "Avg FB Velo (mph)" },
          { key: "spin_rate", label: "Spin Rate (rpm)" },
          { key: "extension", label: "Extension (ft)" },
          { key: "exit_velocity", label: "Exit Velo (mph)" },
          { key: "sixty_time", label: "60-Yard (sec)" },
          { key: "pop_time", label: "Pop Time (sec)" },
          { key: "usage_pct_fb", label: "FB Usage (%)" },
        ].map(({ key, label }) => (
          <div key={key}>
            <label className="block text-xs text-gray-600 mb-1">{label}</label>
            <input
              type="text"
              inputMode="decimal"
              value={metrics[key] || ""}
              onChange={(e) => setMetrics({ ...metrics, [key]: e.target.value })}
              className="w-full px-2 py-1.5 border rounded text-sm"
              data-testid={`edit-metric-${key}`}
            />
          </div>
        ))}
      </div>
      <div className="flex gap-2">
        <Button size="sm" onClick={handleSave} disabled={saving} className="bg-blue-900 text-white">
          <Save className="h-3.5 w-3.5 mr-1" /> {saving ? "Saving..." : "Save Metrics"}
        </Button>
        <Button size="sm" variant="outline" onClick={() => setIsEditing(false)}>Cancel</Button>
      </div>
    </div>
  );
}

export function AdminGradesEditor({ profile, athleteId, onSave }) {
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [grades, setGrades] = useState({});
  const [notes, setNotes] = useState("");

  const startEdit = () => {
    const g = profile.nsb_grades || {};
    setGrades({
      overall: g.overall || "",
      fastball: g.fastball || "",
      curveball: g.curveball || "",
      slider: g.slider || "",
      changeup: g.changeup || "",
      command: g.command || "",
      fielding: g.fielding || "",
      hitting: g.hitting || "",
      power: g.power || "",
      arm_action: g.arm_action || "",
      projectability: g.projectability || "",
      body: g.body || "",
      run_speed: g.run_speed || "",
    });
    setNotes("");
    setIsEditing(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      // Clean: remove empty values, convert to numbers
      const cleaned = {};
      for (const [k, v] of Object.entries(grades)) {
        if (v !== "" && v !== null) cleaned[k] = Number(v);
      }

      // Save grades on player profile
      const res = await fetch(`${API_URL}/api/admin/player/${athleteId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ nsb_grades: cleaned }),
      });

      // Also save as evaluation
      await fetch(`${API_URL}/api/admin/player/${athleteId}/evaluation`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          overall_score: cleaned.overall,
          grades: cleaned,
          written_notes: notes,
          event_name: "Admin Evaluation",
        }),
      });

      if (res.ok) {
        toast.success("Grades saved!");
        setIsEditing(false);
        if (onSave) onSave();
      } else {
        toast.error("Failed to save grades");
      }
    } catch (err) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (!isEditing) {
    return (
      <Button onClick={startEdit} variant="outline" size="sm" data-testid="admin-edit-grades-btn">
        <Edit3 className="h-3.5 w-3.5 mr-1" /> Edit Grades
      </Button>
    );
  }

  return (
    <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg space-y-4" data-testid="grades-edit-panel">
      <h4 className="font-bold text-red-900 text-sm">NSB Grades (20-80 Scale)</h4>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { key: "overall", label: "Overall" },
          { key: "fastball", label: "Fastball" },
          { key: "curveball", label: "Curveball" },
          { key: "slider", label: "Slider" },
          { key: "changeup", label: "Changeup" },
          { key: "command", label: "Command" },
          { key: "fielding", label: "Fielding" },
          { key: "hitting", label: "Hitting" },
          { key: "power", label: "Power" },
          { key: "arm_action", label: "Arm Action" },
          { key: "projectability", label: "Projectability" },
          { key: "body", label: "Body/Frame" },
          { key: "run_speed", label: "Run Speed" },
        ].map(({ key, label }) => (
          <div key={key}>
            <label className="block text-xs text-gray-600 mb-1">{label}</label>
            <input
              type="text"
              inputMode="numeric"
              value={grades[key] || ""}
              onChange={(e) => setGrades({ ...grades, [key]: e.target.value })}
              className="w-full px-2 py-1.5 border rounded text-sm"
              data-testid={`edit-grade-${key}`}
            />
          </div>
        ))}
      </div>
      <div>
        <label className="block text-xs text-gray-600 mb-1">Evaluation Notes</label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={2}
          placeholder="Add scouting notes for this evaluation..."
          className="w-full px-2 py-1.5 border rounded text-sm"
        />
      </div>
      <div className="flex gap-2">
        <Button size="sm" onClick={handleSave} disabled={saving} className="bg-red-600 text-white hover:bg-red-700">
          <Save className="h-3.5 w-3.5 mr-1" /> {saving ? "Saving..." : "Save Grades"}
        </Button>
        <Button size="sm" variant="outline" onClick={() => setIsEditing(false)}>Cancel</Button>
      </div>
    </div>
  );
}
